import type { JSX } from 'react';
import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { App, Dropdown, Tooltip, type MenuProps } from 'antd';
import { DownloadOutlined, DownOutlined, FileExcelOutlined, FilePdfOutlined, FileTextOutlined, PrinterOutlined } from '@ant-design/icons';

import { Button, type ButtonVariant } from './Button';

/**
 * Экспорт «что на экране» (порт CRM `components/report/ExportMenu.tsx`).
 * Страница или вкладка регистрирует, что умеет выгружать, — через props
 * или `useRegisterExport` внутри `ReportExportProvider`. Один формат — сразу
 * кнопка этого формата, несколько — меню «Экспорт ▾», ничего — серая кнопка.
 * CSV собирается в браузере (`exportCsv`), печать — `window.print()`
 * (сайдбар/шапка скрыты @media print в global.css). Excel/PDF — только там,
 * где бэкенд уже отдаёт файл.
 */

export type ExportFormat = 'xlsx' | 'csv' | 'pdf' | 'print';
type Run = () => void | Promise<void>;

export interface ReportExport {
  /** Что уйдёт в файл — подсказка на кнопке («Обучение по точкам за сентябрь»). */
  title: string;
  /** Нельзя выгрузить сейчас (нет данных) — текст причины. */
  disabledReason?: string | null;
  xlsx?: Run;
  csv?: Run;
  pdf?: Run;
  /** true — window.print(), функция — своя печать. */
  print?: boolean | Run;
}

const FORMATS: { key: ExportFormat; label: string; short: string; hint: string; icon: ReactNode }[] = [
  { key: 'xlsx', label: 'Excel (.xlsx)', short: 'Excel', hint: 'Строки как на экране', icon: <FileExcelOutlined /> },
  { key: 'csv', label: 'CSV', short: 'CSV', hint: 'Открывается в Excel, для других систем', icon: <FileTextOutlined /> },
  { key: 'pdf', label: 'PDF', short: 'PDF', hint: 'Для печати и отправки', icon: <FilePdfOutlined /> },
  { key: 'print', label: 'Печать', short: 'Печать', hint: 'Страница без меню и шапки', icon: <PrinterOutlined /> },
];

interface Ctx {
  current: ReportExport | null;
  set: (e: ReportExport | null) => void;
}
const ExportCtx = createContext<Ctx | null>(null);

/** Обернуть страницу с вкладками, чтобы вкладки регистрировали выгрузку для общей кнопки. */
export function ReportExportProvider({ children }: { children: ReactNode }): JSX.Element {
  const [current, set] = useState<ReportExport | null>(null);
  const value = useMemo(() => ({ current, set }), [current]);
  return <ExportCtx.Provider value={value}>{children}</ExportCtx.Provider>;
}

/**
 * Зарегистрировать выгрузку вкладки, пока она смонтирована (`null` — нечего
 * выгружать). Функции берутся свежие через ref — стабильность не нужна.
 */
export function useRegisterExport(exp: ReportExport | null): void {
  const ctx = useContext(ExportCtx);
  const ref = useRef(exp);
  useEffect(() => {
    ref.current = exp;
  });
  const has = !!exp;
  const title = exp?.title ?? '';
  const disabledReason = exp?.disabledReason ?? null;
  const formats = exp ? FORMATS.filter((f) => !!exp[f.key]).map((f) => f.key).join(',') : '';
  const set = ctx?.set;
  useEffect(() => {
    if (!set) return;
    if (!has) {
      set(null);
      return () => set(null);
    }
    const on = new Set(formats.split(','));
    const call = (k: ExportFormat) => () => {
      const fn = ref.current?.[k];
      if (typeof fn === 'function') return fn();
      if (k === 'print' && fn === true) window.print();
    };
    set({
      title,
      disabledReason,
      xlsx: on.has('xlsx') ? call('xlsx') : undefined,
      csv: on.has('csv') ? call('csv') : undefined,
      pdf: on.has('pdf') ? call('pdf') : undefined,
      print: on.has('print') ? call('print') : undefined,
    });
    return () => set(null);
  }, [set, has, title, disabledReason, formats]);
}

export interface ExportMenuProps extends Partial<ReportExport> {
  emptyLabel?: string;
  emptyHint?: string;
  /** Вид кнопки: action — синяя (по DS «Экспорт — синяя кнопка в шапке»), ghost — контурная. */
  variant?: Extract<ButtonVariant, 'action' | 'ghost' | 'primary'>;
  size?: 'sm' | 'md';
}

/** Кнопка экспорта в шапке страницы. Props важнее контекста провайдера. */
export function ExportMenu({ emptyLabel = 'Экспорт', emptyHint = 'На этой странице выгрузки пока нет', variant = 'action', size = 'md', ...own }: ExportMenuProps): JSX.Element {
  const { message } = App.useApp();
  const ctx = useContext(ExportCtx);
  const [busy, setBusy] = useState(false);
  const ownHas = FORMATS.some((f) => !!own[f.key]);
  const exp: ReportExport | null = ownHas ? { ...own, title: own.title ?? '' } : (ctx?.current ?? null);
  const avail = exp ? FORMATS.filter((f) => !!exp[f.key]) : [];
  const disabled = !exp || !avail.length || !!exp.disabledReason;
  const hint = !exp || !avail.length ? emptyHint : (exp.disabledReason ?? exp.title);

  const run = async (k: ExportFormat): Promise<void> => {
    const fn = exp?.[k];
    if (!fn || disabled) return;
    setBusy(true);
    try {
      if (fn === true) window.print();
      else await fn();
    } catch (e) {
      void message.error(`Не удалось выгрузить: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  let btn: ReactNode;
  if (avail.length <= 1) {
    const f = avail[0];
    btn = (
      <Button variant={variant} size={size} icon={<DownloadOutlined />} disabled={disabled} loading={busy} onClick={() => f && void run(f.key)}>
        {f ? f.short : emptyLabel}
      </Button>
    );
  } else {
    const items: MenuProps['items'] = avail.map((f) => ({
      key: f.key,
      icon: f.icon,
      label: (
        <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3, padding: '2px 0' }}>
          <span style={{ fontWeight: 600 }}>{f.label}</span>
          <span style={{ fontSize: 12, opacity: 0.6 }}>{f.hint}</span>
        </span>
      ),
    }));
    btn = (
      <Dropdown disabled={disabled} trigger={['click']} placement="bottomRight" menu={{ items, onClick: ({ key }) => void run(key as ExportFormat) }}>
        <span style={{ display: 'inline-flex' }}>
          <Button variant={variant} size={size} icon={<DownloadOutlined />} iconRight={<DownOutlined style={{ fontSize: 10 }} />} disabled={disabled} loading={busy}>
            Экспорт
          </Button>
        </span>
      </Dropdown>
    );
  }
  // У disabled-кнопки нет событий мыши — подсказку вешаем на обёртку.
  return (
    <Tooltip title={hint}>
      <span className="fk-ds-noprint" style={{ display: 'inline-flex', cursor: disabled ? 'not-allowed' : undefined }}>
        {btn}
      </span>
    </Tooltip>
  );
}
